import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './roadmap.css';
import themeManager from '../themeManager.js';
import { useContent } from '../hooks/useContent';
import { enrichSocialLinks } from '../api/socialIcons';
import { MobileMenuLinks } from '../components/MenuNav';
import HamburgerButton from '../components/HamburgerButton';

const CORNERS = ['tl', 'tr', 'br'];

function renderBullet(text) {
  const parts = text.split('$GUM');
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && <strong>$GUM</strong>}
    </React.Fragment>
  ));
}

export default function Roadmap() {
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = useState(themeManager.getCurrentTheme());
  const { content } = useContent();
  const socialLinks = enrichSocialLinks(content.socials);

  const phases = content.roadmapFull.map((item, i) => ({
    ...item,
    corner: CORNERS[i % CORNERS.length],
  }));

  useEffect(() => {
    const checkTheme = () => setCurrentTheme(themeManager.getCurrentTheme());
    const interval = setInterval(checkTheme, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.body.classList.remove('daytime', 'nighttime');
    document.body.classList.add(currentTheme);

    const hamburger = document.querySelector('.hamburger');
    const panel = document.querySelector('.menu-panel');
    const scrim = document.querySelector('.menu-scrim');
    if (!hamburger || !panel || !scrim) return;

    const menuLinks = panel.querySelectorAll('.menu-link');
    const anchorLinks = document.querySelectorAll('.site-nav a[href^="#"], .menu-link[href^="#"], .menu-link[href^="/#"]');

    function openMenu() {
      document.body.classList.add('menu-open');
      hamburger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      scrim.hidden = false;
    }

    function closeMenu() {
      document.body.classList.remove('menu-open');
      hamburger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      scrim.hidden = true;
    }

    function toggleMenu() {
      document.body.classList.contains('menu-open') ? closeMenu() : openMenu();
    }

    function goHomeHash(hash) {
      navigate('/');
      setTimeout(() => {
        const target = document.getElementById(hash.replace('#', ''));
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }

    function onMenuLinkClick(e) {
      const href = e.currentTarget.getAttribute('href');
      if (href === '/') {
        e.preventDefault();
        navigate('/');
        closeMenu();
      } else if (href === '/roadmap') {
        e.preventDefault();
        closeMenu();
      } else if (href?.startsWith('/#')) {
        e.preventDefault();
        goHomeHash(href.slice(1));
        closeMenu();
      }
    }

    function onAnchorClick(e) {
      const href = e.currentTarget.getAttribute('href');
      if (href?.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
        closeMenu();
      }
    }

    function onHamburgerClick() { toggleMenu(); }

    function onHamburgerKey(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleMenu();
      }
    }

    function onEscape(e) {
      if (e.key === 'Escape' && document.body.classList.contains('menu-open')) {
        closeMenu();
        hamburger.focus();
      }
    }

    hamburger.addEventListener('click', onHamburgerClick);
    scrim.addEventListener('click', closeMenu);
    hamburger.addEventListener('keydown', onHamburgerKey);
    document.addEventListener('keydown', onEscape);
    menuLinks.forEach((link) => link.addEventListener('click', onMenuLinkClick));
    anchorLinks.forEach((link) => link.addEventListener('click', onAnchorClick));

    return () => {
      hamburger.removeEventListener('click', onHamburgerClick);
      scrim.removeEventListener('click', closeMenu);
      hamburger.removeEventListener('keydown', onHamburgerKey);
      document.removeEventListener('keydown', onEscape);
      menuLinks.forEach((link) => link.removeEventListener('click', onMenuLinkClick));
      anchorLinks.forEach((link) => link.removeEventListener('click', onAnchorClick));
    };
  }, [navigate, currentTheme]);

  return (
    <div className="App roadmap-page">
      <header className="site-header">
        <a href="/" className="site-logo" aria-label="Gumball Buddies home">
          <img src="/imgs/gum_logo.png" alt="Gumball Buddies" className="site-logo-img" />
        </a>

        <nav className="site-nav" aria-label="Main navigation">
          <a href="/#about">About</a>
          <a href="/roadmap">Roadmap</a>
          <a href="/announcements">Announcements</a>
          <a href="/media">Media</a>
          <a href="/#socials">Community</a>
        </nav>

        <a href="/#socials" className="site-header-cta">Join Us</a>

        <HamburgerButton />
      </header>

      <nav className="menu-panel" aria-hidden="true">
        <div className="menu-panel-inner">
          <p className="menu-brand">Gumball Buddies</p>
          <ul className="menu-list">
            <MobileMenuLinks
              extraItems={[
                { href: '/announcements', label: 'Announcements', icon: 'megaphone' },
                { href: '/media', label: 'Media', icon: 'media' },
              ]}
            />
          </ul>
        </div>
      </nav>

      <div className="menu-scrim" hidden />

      <main id="roadmap" className="roadmap-wrap">
        <header className="roadmap-page-header">
          <span className="section-eyebrow">{content.roadmapPageHeader.eyebrow}</span>
          <h1 className="roadmap-page-title">{content.roadmapPageHeader.title}</h1>
          <p className="roadmap-page-lead">{content.roadmapPageHeader.lead}</p>
        </header>

        {phases.map((phase) => (
          <section key={phase.phase} id={`phase-${phase.phase}`} className="phase-wrap">
            <article className="phase-card">
              <span className="phase-badge">Phase {phase.phase}</span>
              <h2>
                {phase.title}
                {phase.subtitle && (
                  <span className="phase-meta"> ({phase.subtitle})</span>
                )}
              </h2>
              <ul className="phase-list">
                {(phase.bullets || []).map((bullet) => (
                  <li key={bullet}>{renderBullet(bullet)}</li>
                ))}
              </ul>
              {phase.buddy_image && (
                <img
                  className={`buddy corner ${phase.corner || 'br'}`}
                  src={phase.buddy_image}
                  alt=""
                  aria-hidden="true"
                />
              )}
            </article>
          </section>
        ))}
      </main>

      <footer className="site-footer">
        <div className="footer-inner">
          <a href="/" className="footer-logo" aria-label="Gumball Buddies home">
            <img src="/imgs/gum_logo.png" alt="" />
          </a>
          <nav className="footer-nav" aria-label="Footer navigation">
            <a href="/#about">About</a>
            <a href="/roadmap">Roadmap</a>
            <a href="/announcements">Announcements</a>
            <a href="/media">Media</a>
            <a href="/#socials">Community</a>
          </nav>
          <div className="footer-socials">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                className={`footer-social ${s.cls}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
              >
                <span className="icon" aria-hidden="true">{s.icon}</span>
              </a>
            ))}
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} Gumball Buddies. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
